import { resolveId } from '@medplum/core'
import type {
  Appointment,
  Communication,
  DocumentReference,
  Patient,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { InboxItemType } from '@seen/core/models/inbox'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { type ReactNode, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { match } from 'ts-pattern'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { AppointmentDetails } from '#src/components/appointment/AppointmentDetails.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { DocumentReferenceDisplay } from '#src/components/document-reference/DocumentReferenceDisplay.tsx'
import { MedicationDetailPage } from '#src/components/medications/MedicationDetailPage.tsx'
import { OrderPage } from '#src/components/order/OrderPage.tsx'
import { useCommentParentResource } from '#src/hooks/inbox/useCommentParentResource.ts'
import { useInMemoryStore } from '#src/hooks/useInMemoryStore.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { AppointmentDetailView } from '#src/pages/inbox/detail/AppointmentDetailView.tsx'
import { DetailPanelShell } from '#src/pages/inbox/detail/DetailPanelShell.tsx'
import { FaxErrorDetailView } from '#src/pages/inbox/detail/FaxErrorDetailView.tsx'
import { OrderDetailView } from '#src/pages/inbox/detail/OrderDetailView.tsx'
import { ResourceUnavailable } from '#src/pages/inbox/detail/ResourceUnavailable.tsx'
import { SidebarHeader } from '#src/pages/inbox/detail/SidebarHeader.tsx'
import { TaskDetailView } from '#src/pages/inbox/detail/TaskDetailView.tsx'
import { TaskFocusContent } from '#src/pages/inbox/detail/TaskFocusContent.tsx'
import {
  snippetText,
  taskFocusTitle,
  taskSidebarHeaderProps,
} from '#src/pages/inbox/detail/taskTitles.ts'

interface InboxDetailPanelProps {
  item: NotificationItem
  onClose: () => void
}

export function InboxDetailPanel({
  item,
  onClose,
}: InboxDetailPanelProps): ReactNode {
  return match(item.data)
    .with({ type: InboxItemType.TaskAssigned }, (data) => (
      <TaskDetailView
        taskRef={data.task}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.TaskDue }, (data) => (
      <TaskDetailView
        taskRef={data.task}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.TaskNewComment }, (data) => (
      <TaskDetailView
        taskRef={data.task}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.OrderSigned }, (data) => (
      <OrderDetailView
        orderRef={data.order}
        actionUrl={data.actionUrl}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.NewProposedAppointment }, (data) => (
      <AppointmentDetailView
        appointmentRef={data.appointment}
        actionUrl={data.actionUrl}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.NewComment }, (data) => (
      <CommentDetailView
        commentRef={data.comment}
        partOfResourceType={data.partOfResourceType}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.NewCommentReply }, (data) => (
      <CommentDetailView
        commentRef={data.comment}
        partOfResourceType={data.partOfResourceType}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.CommentMention }, (data) => (
      <CommentDetailView
        commentRef={data.comment}
        partOfResourceType={data.partOfResourceType}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .with({ type: InboxItemType.FaxError }, (data) => (
      <FaxErrorDetailView
        item={data}
        notificationItem={item}
        onClose={onClose}
      />
    ))
    .exhaustive()
}

function commentResourceTypeLabel(resource: Resource | undefined): string {
  if (!resource) {
    return 'Discussion'
  }
  if (isOfType(resource, 'MedicationRequest')) {
    return 'Comment on medication'
  }
  if (isOfType(resource, 'Appointment')) {
    return 'Comment on appointment'
  }
  if (isOfType(resource, 'Patient')) {
    return 'Comment on patient'
  }
  if (isOfType(resource, 'CarePlan')) {
    return 'Comment on care plan'
  }
  return 'Discussion'
}

/* Derive action-oriented title for comment-based notifications */
function useCommentSidebarTitle(
  notificationItem: NotificationItem,
  comment: { sender?: Reference; message?: string } | undefined
): string {
  const senderRef =
    comment?.sender && isOfType(comment.sender, 'Practitioner')
      ? comment.sender
      : undefined
  const { data: sender } = useRead(ExtendedPractitioner, senderRef)

  const snippet = snippetText(comment?.message)
  const { type } = notificationItem.data
  if (type === InboxItemType.CommentMention) {
    if (sender && snippet) {
      return `${sender.name} mentioned you: ${snippet}`
    }
    return sender ? `${sender.name} mentioned you` : 'You were mentioned'
  }
  if (type === InboxItemType.NewCommentReply) {
    if (sender && snippet) {
      return `${sender.name}: ${snippet}`
    }
    return sender ? `${sender.name} replied` : 'New reply'
  }
  if (sender && snippet) {
    return `${sender.name}: ${snippet}`
  }
  return sender ? `${sender.name} commented` : 'New comment'
}

/* Comment viewer -- resolves to parent resource */
function CommentDetailView({
  commentRef,
  partOfResourceType,
  notificationItem,
  onClose,
}: {
  commentRef: Reference<Communication>
  partOfResourceType?: string
  notificationItem: NotificationItem
  onClose: () => void
}): ReactNode {
  const commentId = resolveId(commentRef)

  const { comment, resource, relatedTask, relatedOrder, patient, isLoading } =
    useCommentParentResource(commentRef)
  const commentTitle = useCommentSidebarTitle(notificationItem, comment)

  const commentUrl = useTimelineItemUrl(comment)

  const setHighlightCommentId = useInMemoryStore((s) => s.setHighlightCommentId)
  const highlightTarget =
    !isLoading && commentId ? `comment-${commentId}` : undefined
  useEffect(() => {
    if (highlightTarget) {
      setHighlightCommentId(highlightTarget)
    }
    return () => {
      if (useInMemoryStore.getState().highlightCommentId === highlightTarget) {
        setHighlightCommentId(null)
      }
    }
  }, [highlightTarget, setHighlightCommentId])

  if (isLoading) {
    return (
      <DetailPanelShell onClose={onClose}>
        <Loader />
      </DetailPanelShell>
    )
  }

  /* Parent resource couldn't be resolved -- show comment text as fallback */
  if (!resource) {
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <SidebarHeader
          title={commentTitle}
          notificationItem={notificationItem}
          onClose={onClose}
        />
        <div className="space-y-3 p-4">
          {!!comment?.message && (
            <div className="text-sm whitespace-pre-wrap">{comment.message}</div>
          )}
          {!comment?.message && (
            <div className="text-sm text-muted-foreground">
              Unable to load the parent resource for this discussion
            </div>
          )}
        </div>
      </div>
    )
  }

  /* Task -- show the task's focus resource, not the full task chrome */
  if (
    partOfResourceType === 'Task' ||
    relatedTask ||
    StandardTask.isInstance(resource)
  ) {
    const task =
      relatedTask ??
      (isOfType(resource, 'Task')
        ? StandardTask.fromFhirResource(resource)
        : undefined)
    if (!task) {
      return (
        <DetailPanelShell onClose={onClose}>
          <div className="text-sm text-muted-foreground">
            Unable to display task
          </div>
        </DetailPanelShell>
      )
    }
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <SidebarHeader
          title={commentTitle}
          subtitle={
            patient
              ? `${taskFocusTitle(task)} \u00b7 ${patient.name}`
              : taskFocusTitle(task)
          }
          {...taskSidebarHeaderProps(task)}
          notificationItem={notificationItem}
          onClose={onClose}
        />
        <ScrollArea
          className="flex-1"
          scrollRestorationId="inbox-detail-comment-task"
        >
          <div className="p-4">
            <ErrorBoundary fallback={<ResourceUnavailable label="resource" />}>
              <TaskFocusContent task={task} includeActivity />
            </ErrorBoundary>
          </div>
        </ScrollArea>
      </div>
    )
  }

  /* Order (ServiceRequest) */
  if (
    partOfResourceType === 'ServiceRequest' ||
    relatedOrder ||
    isOfType(resource, 'ServiceRequest')
  ) {
    const order =
      relatedOrder ??
      (isOfType(resource, 'ServiceRequest') ? resource : undefined)
    const orderContextUrl =
      patient && order ? `/Patient/${patient.id}/orders/${order.id}` : undefined
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <SidebarHeader
          title={commentTitle}
          subtitle={patient ? `Order \u00b7 ${patient.name}` : 'Order'}
          actionUrl={orderContextUrl}
          actionLabel="Open chart"
          notificationItem={notificationItem}
          onClose={onClose}
        />
        <ScrollArea
          className="flex-1"
          scrollRestorationId="inbox-detail-comment-order"
        >
          <div className="p-4">
            <ErrorBoundary fallback={<ResourceUnavailable label="order" />}>
              <OrderPage orderId={order?.id} hideMaximize />
            </ErrorBoundary>
          </div>
        </ScrollArea>
      </div>
    )
  }

  /* DocumentReference */
  if (
    partOfResourceType === 'DocumentReference' ||
    isOfType(resource, 'DocumentReference')
  ) {
    if (!isOfType(resource, 'DocumentReference')) {
      return (
        <DetailPanelShell onClose={onClose}>
          <div className="text-sm text-muted-foreground">
            Unable to display document
          </div>
        </DetailPanelShell>
      )
    }
    const docRefReference = {
      reference: `DocumentReference/${resource.id}`,
    } as Reference<DocumentReference>
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <SidebarHeader
          title={commentTitle}
          subtitle={patient ? `Document \u00b7 ${patient.name}` : 'Document'}
          notificationItem={notificationItem}
          onClose={onClose}
        />
        <ScrollArea
          className="flex-1"
          scrollRestorationId="inbox-detail-comment-doc"
        >
          <div className="p-4">
            <ErrorBoundary fallback={<ResourceUnavailable label="document" />}>
              <DocumentReferenceDisplay
                documentReference={docRefReference}
                hideDelete
              />
            </ErrorBoundary>
          </div>
        </ScrollArea>
      </div>
    )
  }

  /* MedicationRequest */
  if (
    partOfResourceType === 'MedicationRequest' ||
    isOfType(resource, 'MedicationRequest')
  ) {
    if (!isOfType(resource, 'MedicationRequest')) {
      return (
        <DetailPanelShell onClose={onClose}>
          <div className="text-sm text-muted-foreground">
            Unable to display medication
          </div>
        </DetailPanelShell>
      )
    }
    return (
      <MedicationRequestCommentView
        medicationRequestId={resource.id}
        patientName={patient?.name}
        commentTitle={commentTitle}
        notificationItem={notificationItem}
        onClose={onClose}
      />
    )
  }

  /* Appointment */
  if (
    partOfResourceType === 'Appointment' ||
    isOfType(resource, 'Appointment')
  ) {
    if (!isOfType(resource, 'Appointment')) {
      return (
        <DetailPanelShell onClose={onClose}>
          <div className="text-sm text-muted-foreground">
            Unable to display appointment
          </div>
        </DetailPanelShell>
      )
    }
    const appointmentRef = {
      reference: `Appointment/${resource.id}`,
    } as Reference<Appointment>
    const appointmentUrl =
      commentUrl && commentUrl !== '/' ? commentUrl : undefined
    return (
      <div className="flex h-full flex-col border-l border-border bg-background">
        <SidebarHeader
          title={commentTitle}
          subtitle={
            patient ? `Appointment \u00b7 ${patient.name}` : 'Appointment'
          }
          actionUrl={appointmentUrl}
          actionLabel="Open calendar"
          notificationItem={notificationItem}
          onClose={onClose}
        />
        <div className="min-h-0 flex-1">
          <ErrorBoundary fallback={<ResourceUnavailable label="appointment" />}>
            <AppointmentDetails appointmentRef={appointmentRef} />
          </ErrorBoundary>
        </div>
      </div>
    )
  }

  /* Patient -- timeline comment thread */
  if (isOfType(resource, 'Patient')) {
    return (
      <PatientCommentDetailView
        comment={comment}
        commentId={commentId}
        commentTitle={commentTitle}
        resource={resource as Patient}
        notificationItem={notificationItem}
        onClose={onClose}
      />
    )
  }

  /* Fallback for resource types without a specialized view */
  const fallbackContextUrl =
    commentUrl && commentUrl !== '/' ? commentUrl : undefined
  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <SidebarHeader
        title={commentTitle}
        subtitle={commentResourceTypeLabel(resource)}
        contextUrl={fallbackContextUrl}
        notificationItem={notificationItem}
        onClose={onClose}
      />
      <ScrollArea
        className="flex-1"
        scrollRestorationId="inbox-detail-comment-generic"
      >
        <div className="p-4">
          {!!comment && <ActivityComment comment={comment} />}
          {!comment && (
            <div className="text-sm text-muted-foreground">
              Unable to load comment
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

/* Patient timeline comment viewer -- resolves root comment for full thread */
function PatientCommentDetailView({
  comment,
  commentId,
  commentTitle,
  resource,
  notificationItem,
  onClose,
}: {
  comment: CommentCommunication | undefined
  commentId: string | undefined
  commentTitle: string
  resource: Patient
  notificationItem: NotificationItem
  onClose: () => void
}): ReactNode {
  const participant = Participant.fromFhirResource(resource)

  /* If this is a reply, fetch the root comment so we render the full thread */
  const { data: rootComment, isLoading: isLoadingRoot } = useRead(
    CommentCommunication,
    comment?.parentComment
  )

  const timelineUrl = commentId
    ? `${participant.relativeUrl}#item-${commentId}`
    : undefined
  const threadComment = comment?.parentComment ? rootComment : comment

  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <SidebarHeader
        title={commentTitle}
        subtitle={participant?.name}
        actionUrl={timelineUrl}
        actionLabel="Open chart"
        notificationItem={notificationItem}
        onClose={onClose}
      />
      <ScrollArea
        className="flex-1"
        scrollRestorationId="inbox-detail-comment-timeline"
      >
        <div className="p-4">
          {isLoadingRoot && <Loader />}
          {!isLoadingRoot && !!threadComment && (
            <ActivityComment comment={threadComment} />
          )}
          {!isLoadingRoot && !threadComment && !comment && (
            <div className="text-sm text-muted-foreground">
              Unable to load comment
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

/* Medication comment viewer -- embeds MedicationDetailPage like OrderPage */
function MedicationRequestCommentView({
  medicationRequestId,
  patientName,
  commentTitle,
  notificationItem,
  onClose,
}: {
  medicationRequestId: string | undefined
  patientName: string | undefined
  commentTitle: string
  notificationItem: NotificationItem
  onClose: () => void
}): ReactNode {
  const { data: medicationRequest } = useRead(
    ExtendedMedicationRequest,
    medicationRequestId
  )

  const patientId = resolveId(medicationRequest?.subject)
  const medContextUrl =
    patientId && medicationRequest?.id
      ? `/Patient/${patientId}/medications/${medicationRequest.id}`
      : undefined

  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <SidebarHeader
        title={commentTitle}
        subtitle={
          patientName
            ? `${medicationRequest?.medicationDisplay ?? 'Medication'} \u00b7 ${patientName}`
            : (medicationRequest?.medicationDisplay ?? 'Medication')
        }
        actionUrl={medContextUrl}
        actionLabel="Open chart"
        notificationItem={notificationItem}
        onClose={onClose}
      />
      <ScrollArea
        className="flex-1"
        scrollRestorationId="inbox-detail-comment-medication"
      >
        <div className="p-4">
          <ErrorBoundary fallback={<ResourceUnavailable label="medication" />}>
            <MedicationDetailPage
              medicationId={medicationRequestId}
              hideHeader
            />
          </ErrorBoundary>
        </div>
      </ScrollArea>
    </div>
  )
}
